import React from 'react';
import { Award, Users, Heart, Target, Quote, Sparkles, CheckCircle2 } from 'lucide-react';
import founder from "../../assets/founder.webp";
import { SEO } from '../SEO';

const FounderPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#FAF8F3] text-gray-900 selection:bg-[#2C4A3B] selection:text-white">
      <SEO
        title="Meet Our Founder | MetaFit Wellness Leadership"
        description="Learn about the vision, story, and mission behind MetaFit Wellness from founder Meenakshi Paliwal — empowering holistic health and conscious wellness."
        canonical="/founder"
        keywords="MetaFit founder, Meenakshi Paliwal, wellness leadership, holistic health story, MetaFit Wellness"
      />

      {/* Unified Hero & Founder Spotlight Section - Zero Wasted Space */}
      <div className="relative pt-5 pb-6 sm:pt-6 sm:pb-10 md:pt-8 md:pb-12 overflow-hidden bg-gradient-to-b from-[#F2ECE1]/60 via-[#FAF8F3] to-[#FAF8F3] border-b border-[#2C4A3B]/10">
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#2C4A3B]/10 blur-3xl" />
          <div className="absolute top-0 -right-20 w-72 h-72 rounded-full bg-[#C69248]/10 blur-3xl" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 max-w-6xl relative z-10">
          {/* Compact Top Heading */}
          <div className="text-center max-w-3xl mx-auto mb-4 sm:mb-6 md:mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-0.5 sm:py-1 rounded-full text-[11px] sm:text-xs font-semibold tracking-wider uppercase bg-[#E8F0EC] text-[#2C4A3B] border border-[#2C4A3B]/15 shadow-sm mb-1.5 sm:mb-2.5">
              <Sparkles className="w-3.5 h-3.5 text-[#C69248]" />
              <span>Leadership & Vision</span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[42px] font-extrabold text-[#15302B] tracking-tight leading-tight">
              Visionary Behind the{" "}
              <span className="bg-gradient-to-r from-[#2C4A3B] via-[#3F6652] to-[#24483C] bg-clip-text text-transparent">
                Wellness Revolution
              </span>
            </h1>

            <p className="text-xs sm:text-sm md:text-base text-[#3B5750] leading-relaxed max-w-2xl mx-auto mt-1.5 sm:mt-2">
              Meet the passionate leader who transformed her vision into a movement that has touched thousands of lives
            </p>
          </div>

          {/* Founder Profile Grid - Directly visible above the fold */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 items-center">
            
            {/* Left: Founder Portrait with Floating Highlights */}
            <div className="lg:col-span-5 relative max-w-sm sm:max-w-md mx-auto lg:max-w-none w-full">
              <div className="relative rounded-3xl p-2 sm:p-2.5 bg-gradient-to-br from-[#2C4A3B]/15 via-[#C69248]/15 to-[#2C4A3B]/10 shadow-xl border border-white/80">
                <div className="relative rounded-2xl overflow-hidden aspect-square sm:aspect-[4/4.5] bg-gray-100 shadow-inner">
                  <img
                    src={founder}
                    alt="Meenakshi Paliwal - Founder"
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500 ease-out"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Bottom Image Overlay Label */}
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <p className="text-base sm:text-lg font-bold drop-shadow-md">Meenakshi Paliwal</p>
                    <p className="text-xs text-white/90 drop-shadow-sm">Founder & Wellness Innovator</p>
                  </div>
                </div>

                {/* Floating Stat Badge */}
                <div className="absolute -bottom-3 -right-2 sm:-bottom-4 sm:-right-3 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl p-2.5 sm:p-3.5 border border-[#2C4A3B]/15 flex items-center gap-2.5 sm:gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0">
                    <Heart className="h-5 w-5 text-[#2C4A3B] fill-[#2C4A3B]/20" />
                  </div>
                  <div>
                    <p className="text-lg sm:text-xl font-black text-[#15302B] leading-none">1000+</p>
                    <p className="text-[10px] sm:text-xs text-[#3B5750] font-medium mt-0.5">Lives Transformed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Bio & Profile Details */}
            <div className="lg:col-span-7 space-y-6 sm:space-y-7 mt-4 lg:mt-0">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#C69248]">
                  About The Founder
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#15302B] tracking-tight mt-1 mb-3">
                  Meet <span className="text-[#2C4A3B]">Meenakshi Paliwal</span>
                </h2>

                {/* Badges / Roles */}
                <div className="flex flex-wrap gap-2 pt-1">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-[#2C4A3B] text-white shadow-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                    The Founder - Meta Fit Wellness
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs sm:text-sm font-semibold bg-[#FFF3E0] text-[#B76E00] border border-[#FFE0B2]">
                    Fit India Ambassador (Rajasthan)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs sm:text-sm font-medium bg-[#E8F0EC] text-[#2C4A3B] border border-[#2C4A3B]/15">
                    IT Professional | Fitness Coach | Changemaker
                  </span>
                </div>
              </div>

              {/* Bio Narrative */}
              <div className="space-y-4 text-sm sm:text-base text-[#2E423B] leading-relaxed">
                <p className="bg-white/70 p-4 sm:p-5 rounded-2xl border border-[#2C4A3B]/10 shadow-sm">
                  <strong className="text-[#15302B]">Meenakshi Paliwal</strong> is a powerful example of passion meeting purpose. A software engineer with 
                  an MSc in IT, she began her professional journey in a stable government position at Jawahar 
                  Navodaya Vidyalaya. But the real calling of her life was transformation—of mind, body, and community.
                </p>
                
                <p className="bg-white/70 p-4 sm:p-5 rounded-2xl border border-[#2C4A3B]/10 shadow-sm">
                  Driven by her vision, she left her government job to pursue her passion in the fitness and 
                  wellness industry, where she has now thrived for over 10 years.
                </p>
              </div>

              {/* Quick Metrics */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-1">
                <div className="bg-gradient-to-br from-[#E8F0EC] to-white p-4 sm:p-5 rounded-2xl border border-[#2C4A3B]/15 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-2xl sm:text-3xl font-extrabold text-[#2C4A3B]">10+</div>
                  <div className="text-xs sm:text-sm text-[#3B5750] font-medium mt-1">Years Experience</div>
                </div>
                <div className="bg-gradient-to-br from-[#FFF8E7] to-white p-4 sm:p-5 rounded-2xl border border-[#C69248]/25 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-2xl sm:text-3xl font-extrabold text-[#B76E00]">4</div>
                  <div className="text-xs sm:text-sm text-[#3B5750] font-medium mt-1">Startups Founded</div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </div>

      {/* Credentials Section */}
      <div className="py-8 sm:py-12 md:py-14 bg-[#F4EFE6]/60 border-y border-[#2C4A3B]/10">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-[#C69248]">
              Expertise & Milestones
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#15302B] tracking-tight mt-1">
              Today, she stands tall as:
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            
            <div className="group bg-white p-6 sm:p-7 rounded-2xl shadow-sm hover:shadow-xl border border-[#2C4A3B]/10 hover:border-[#2C4A3B]/30 hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#E8F0EC] group-hover:bg-[#2C4A3B] rounded-2xl flex items-center justify-center mb-4 transition-colors duration-300 shadow-sm">
                <Award className="h-7 w-7 text-[#2C4A3B] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-[#15302B] leading-snug">
                Certified Yoga Trainer
              </h3>
            </div>
            
            <div className="group bg-white p-6 sm:p-7 rounded-2xl shadow-sm hover:shadow-xl border border-[#2C4A3B]/10 hover:border-[#2C4A3B]/30 hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#EBF7F2] group-hover:bg-emerald-700 rounded-2xl flex items-center justify-center mb-4 transition-colors duration-300 shadow-sm">
                <Heart className="h-7 w-7 text-emerald-700 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-[#15302B] leading-snug">
                Nutrition & Wellness Consultant
              </h3>
            </div>
            
            <div className="group bg-white p-6 sm:p-7 rounded-2xl shadow-sm hover:shadow-xl border border-[#2C4A3B]/10 hover:border-[#2C4A3B]/30 hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#E8F0EC] group-hover:bg-[#2C4A3B] rounded-2xl flex items-center justify-center mb-4 transition-colors duration-300 shadow-sm">
                <Users className="h-7 w-7 text-[#2C4A3B] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-[#15302B] leading-snug">
                Founder of Meta Fit Wellness and Miss Fitness Coach
              </h3>
            </div>
            
            <div className="group bg-white p-6 sm:p-7 rounded-2xl shadow-sm hover:shadow-xl border border-[#2C4A3B]/10 hover:border-[#2C4A3B]/30 hover:-translate-y-1 transition-all duration-300 flex flex-col items-center text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#FFF8E7] group-hover:bg-[#B76E00] rounded-2xl flex items-center justify-center mb-4 transition-colors duration-300 shadow-sm">
                <Target className="h-7 w-7 text-[#B76E00] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-[#15302B] leading-snug">
                Entrepreneur across 4 startups
              </h3>
            </div>

          </div>
        </div>
      </div>

      {/* Impact Story Section & Inspiring Quote */}
      <div className="py-8 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <div className="text-center mb-10 sm:mb-12">
            <span className="text-xs font-bold uppercase tracking-wider text-[#C69248]">
              Creating Meaningful Change
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#15302B] tracking-tight mt-1">
              The Impact Story
            </h2>
          </div>
          
          <div className="space-y-5 text-base sm:text-lg text-[#2E423B] leading-relaxed">
            <p className="bg-[#FAF8F3] p-5 sm:p-6 rounded-2xl border border-[#2C4A3B]/10">
              Through her coaching, online platforms, and wellness camps, Meenakshi has changed over 1000 
              lives across India, offering practical, natural, and empowering fitness solutions. She continues to 
              balance her tech skills with purpose-driven wellness innovations—also managing part-time IT 
              projects to stay ahead in both worlds.
            </p>
            
            <p className="bg-[#FAF8F3] p-5 sm:p-6 rounded-2xl border border-[#2C4A3B]/10">
              Her mission is simple yet profound: <strong className="text-[#15302B]">Make India healthier, stronger, and more self-aware—one 
              person at a time.</strong>
            </p>
            
            <p className="bg-[#FAF8F3] p-5 sm:p-6 rounded-2xl border border-[#2C4A3B]/10">
              She has received numerous awards and milestones, but for Meenakshi, the true reward lies in the 
              stories of people reclaiming their health, energy, and confidence.
            </p>
          </div>
          
          {/* Quote Card */}
          <div className="relative overflow-hidden bg-gradient-to-br from-[#1E3E34] via-[#24483C] to-[#15302B] text-white rounded-3xl p-7 sm:p-10 md:p-12 mt-10 sm:mt-14 shadow-2xl">
            <div className="absolute top-4 right-6 text-white/10 pointer-events-none">
              <Quote className="w-24 h-24 sm:w-32 sm:h-32 -rotate-12" />
            </div>

            <div className="relative z-10 max-w-2xl mx-auto text-center">
              <Quote className="w-8 h-8 text-[#C69248] mx-auto mb-4 opacity-90" />
              <blockquote className="text-lg sm:text-xl md:text-2xl font-medium text-[#FAF8F3] mb-6 italic leading-relaxed">
                "I didn't just switch careers—I chose impact over comfort. Every transformation I lead reminds me 
                why I began."
              </blockquote>
              <div className="text-base sm:text-lg font-bold text-[#D4A373] tracking-wide">
                — Meenakshi Paliwal
              </div>
              <div className="text-xs sm:text-sm text-[#E8E1CF]/80 mt-1">
                Founder, MetaFit Wellness
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fit India Ambassador Badge - Prestigious Recognition */}
      <div className="py-8 sm:py-12 bg-gradient-to-b from-white via-[#F7F5EE] to-[#FAF8F3] border-t border-[#2C4A3B]/10">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white rounded-3xl sm:rounded-full px-6 sm:px-10 py-5 sm:py-4 shadow-xl border border-[#2C4A3B]/15 hover:shadow-2xl transition-all duration-300">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center shrink-0 shadow-sm border border-amber-200">
              <Award className="h-7 w-7 text-amber-700" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-base sm:text-lg font-bold text-[#15302B]">
                Proudly, a Fit India Ambassador
              </p>
              <p className="text-xs sm:text-sm text-[#3B5750]">
                For Rajasthan • Championing Health & Wellness Nationally
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default FounderPage;